import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage, BlockSequenceConfig, CodeBlockItem } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import {
    RotateCcw,
    Bug,
    Trash2,
    Plus,
    Sparkles,
} from 'lucide-react-native';

interface BlockSequenceActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

interface RobotState {
    x: number;
    y: number;
    heading: 0 | 90 | 180 | 270; // 0 = up/north, 90 = right/east, 180 = down/south, 270 = left/west
}

const THEMES = {
    mars_rover: {
        sprite: '🛸',
        targetIcon: '📡',
        targetName: 'Sensor Array',
        obstacleIcon: '☄️',
        bgCellColor: '#FFF1F2',
        gridBorderColor: '#FECDD3',
        title: 'Mars Rover Expedition',
    },
    drone: {
        sprite: '🚁',
        targetIcon: '🏁',
        targetName: 'Landing Pad',
        obstacleIcon: '🏢',
        bgCellColor: '#F0FDF4',
        gridBorderColor: '#BBF7D0',
        title: 'Drone Navigation Lab',
    },
    turtle: {
        sprite: '🐢',
        targetIcon: '🏝️',
        targetName: 'Oasis Lagoon',
        obstacleIcon: '🪨',
        bgCellColor: '#ECFEFF',
        gridBorderColor: '#A5F3FC',
        title: 'Turtle Geometry Path',
    },
    submersible: {
        sprite: '🚢',
        targetIcon: '💎',
        targetName: 'Sunken Treasure',
        obstacleIcon: '🪸',
        bgCellColor: '#EFF6FF',
        gridBorderColor: '#BFDBFE',
        title: 'Ocean Trench Explorer',
    },
};

export const BlockSequenceActivity: React.FC<BlockSequenceActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as BlockSequenceConfig;
    const themeKey = (config.theme && THEMES[config.theme] ? config.theme : 'mars_rover') as keyof typeof THEMES;
    const theme = THEMES[themeKey];

    const rows = Math.max(config.gridSize?.rows || 4, 3);
    const cols = Math.max(config.gridSize?.cols || 4, 3);
    const startPos = config.startPosition || { x: 0, y: rows - 1 };
    const targetPos = config.targetPosition || { x: cols - 1, y: 0 };
    const obstacles = useMemo(() => config.obstacles || [], [config.obstacles]);

    const defaultBlocks: CodeBlockItem[] = useMemo(() => {
        return (
            config.availableBlocks || [
                { id: 'b_move_1', type: 'move', label: 'MOVE Forward (1)', param: 1, color: '#3B82F6' },
                { id: 'b_move_2', type: 'move', label: 'MOVE Forward (2)', param: 2, color: '#2563EB' },
                { id: 'b_turn_r', type: 'turn_right', label: 'TURN Right (90°)', param: 90, color: '#8B5CF6' },
                { id: 'b_turn_l', type: 'turn_left', label: 'TURN Left (90°)', param: 90, color: '#7C3AED' },
                { id: 'b_repeat_2', type: 'repeat', label: 'REPEAT (2x)', param: 2, color: '#EA580C' },
            ]
        );
    }, [config.availableBlocks]);

    // Initial program sequence
    const [program, setProgram] = useState<CodeBlockItem[]>(() => {
        if (config.initialCode && config.initialCode.length > 0) {
            return config.initialCode.map((b, i) => ({ ...b, id: `init_${i}_${b.type}` }));
        }
        return [];
    });

    const isDebugMode = config.mode === 'debug';

    const [robot, setRobot] = useState<RobotState>({
        x: startPos.x,
        y: startPos.y,
        heading: config.startHeading !== undefined ? config.startHeading : 90,
    });

    const [activeBlockIndex, setActiveBlockIndex] = useState<number | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        isDebugMode ? 'Bug Buster: Step through code and fix the error!' : 'Assemble instruction blocks and press RUN!'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    // Flatten repeat blocks into individual executable steps for simulation
    const flattenedSteps = useMemo(() => {
        const steps: Array<{ blockIndex: number; action: CodeBlockItem }> = [];
        let i = 0;
        while (i < program.length) {
            const block = program[i];
            if (block.type === 'repeat' && i + 1 < program.length) {
                const count = block.param || 2;
                const nextBlock = program[i + 1];
                for (let c = 0; c < count; c++) {
                    steps.push({ blockIndex: i, action: nextBlock });
                }
                i += 2;
            } else {
                steps.push({ blockIndex: i, action: block });
                i++;
            }
        }
        return steps;
    }, [program]);

    const addBlock = (block: CodeBlockItem) => {
        if (isRunning || hasCompleted) return;
        setProgram((prev) => [...prev, { ...block, id: `prog_${Date.now()}_${Math.random().toString(36).substr(2, 4)}` }]);
        setTrafficStatus('idle');
    };

    const removeBlock = (index: number) => {
        if (isRunning || hasCompleted) return;
        setProgram((prev) => prev.filter((_, i) => i !== index));
        setTrafficStatus('idle');
    };

    const resetSimulation = () => {
        setIsRunning(false);
        setActiveBlockIndex(null);
        setRobot({
            x: startPos.x,
            y: startPos.y,
            heading: config.startHeading !== undefined ? config.startHeading : 90,
        });
        setTrafficStatus('idle');
        setStatusMessage(isDebugMode ? 'Find and fix the bug in the algorithm!' : 'Ready to run.');
    };

    const runSimulation = async () => {
        if (isRunning || hasCompleted || program.length === 0) return;
        setIsRunning(true);
        setTrafficStatus('busy');
        setStatusMessage('Executing program algorithm...');

        let currentRobot: RobotState = {
            x: startPos.x,
            y: startPos.y,
            heading: config.startHeading !== undefined ? config.startHeading : 90,
        };
        setRobot(currentRobot);

        for (let s = 0; s < flattenedSteps.length; s++) {
            const { blockIndex, action } = flattenedSteps[s];
            setActiveBlockIndex(blockIndex);

            // Execute action
            const nextRobot = { ...currentRobot };
            if (action.type === 'move') {
                const steps = action.param || 1;
                for (let step = 0; step < steps; step++) {
                    let dx = 0;
                    let dy = 0;
                    if (nextRobot.heading === 0) dy = -1;
                    else if (nextRobot.heading === 90) dx = 1;
                    else if (nextRobot.heading === 180) dy = 1;
                    else if (nextRobot.heading === 270) dx = -1;

                    const newX = nextRobot.x + dx;
                    const newY = nextRobot.y + dy;

                    // Boundary check
                    if (newX < 0 || newX >= cols || newY < 0 || newY >= rows) {
                        setTrafficStatus('incorrect');
                        setStatusMessage('OutOfBounds Error! Rover ran off the grid boundaries.');
                        setIsRunning(false);
                        return;
                    }

                    // Obstacle check
                    const hitObstacle = obstacles.some((o) => o.x === newX && o.y === newY);
                    if (hitObstacle) {
                        setTrafficStatus('incorrect');
                        setStatusMessage(`Collision Error! Crashed into obstacle at (${newX}, ${newY}).`);
                        setIsRunning(false);
                        return;
                    }

                    nextRobot.x = newX;
                    nextRobot.y = newY;
                }
            } else if (action.type === 'turn_right') {
                nextRobot.heading = ((nextRobot.heading + 90) % 360) as 0 | 90 | 180 | 270;
            } else if (action.type === 'turn_left') {
                nextRobot.heading = ((nextRobot.heading + 270) % 360) as 0 | 90 | 180 | 270;
            }

            currentRobot = nextRobot;
            setRobot(currentRobot);

            // Delay for animated step
            await new Promise((resolve) => setTimeout(resolve, 350));
        }

        setIsRunning(false);
        setActiveBlockIndex(null);

        // Check if reached target
        if (currentRobot.x === targetPos.x && currentRobot.y === targetPos.y) {
            setTrafficStatus('correct');
            setStatusMessage('Algorithm Success! Target beacon reached.');
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: 'completed',
                mechanic: 'block_sequence',
                isCorrect: true,
                score: 100,
                program: program.map((b) => b.label),
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('incorrect');
            setStatusMessage(`Ended at (${currentRobot.x}, ${currentRobot.y}) - Did not reach target beacon.`);
        }
    };

    return (
        <ScrollView
            style={styles.scrollWrapper}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
        >
            <StageCard testID="block-sequence-card">
                <View style={styles.container}>
                {/* Header with Traffic Light and Prompt */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badgeRow}>
                            <View style={[styles.badge, isDebugMode && styles.badgeDebug]}>
                                {isDebugMode ? (
                                    <Bug size={14} color="#DC2626" />
                                ) : (
                                    <Sparkles size={14} color="#4F46E5" />
                                )}
                                <Text style={[styles.badgeText, isDebugMode && styles.badgeTextDebug]}>
                                    {isDebugMode ? 'Bug Buster Mode' : 'Block Logic Algorithm'}
                                </Text>
                            </View>
                            <Text style={styles.themeTitle}>{theme.title}</Text>
                        </View>
                        <Text style={styles.goalText}>{config.goal || 'Build an algorithm to navigate to the target beacon.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {config.audioPromptText && (
                            <AudioPromptButton
                                promptText={config.audioPromptText}
                                activityId={activity.id}
                                testID="block-sequence-audio-button"
                            />
                        )}
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Main Workspace: Left Grid Terrain, Right Block Script */}
                <View style={styles.workspace}>
                    {/* Grid Simulation */}
                    <View style={styles.gridColumn}>
                        <View style={[styles.gridContainer, { borderColor: theme.gridBorderColor }]}>
                            {Array.from({ length: rows }).map((_, r) => (
                                <View key={`row_${r}`} style={styles.gridRow}>
                                    {Array.from({ length: cols }).map((_, c) => {
                                        const isRobotHere = robot.x === c && robot.y === r;
                                        const isTargetHere = targetPos.x === c && targetPos.y === r;
                                        const isObstacleHere = obstacles.some((o) => o.x === c && o.y === r);

                                        return (
                                            <View
                                                key={`cell_${r}_${c}`}
                                                style={[
                                                    styles.gridCell,
                                                    { backgroundColor: theme.bgCellColor },
                                                    isTargetHere && styles.targetCell,
                                                ]}
                                            >
                                                {isObstacleHere && (
                                                    <Text style={styles.cellIcon}>{theme.obstacleIcon}</Text>
                                                )}
                                                {isTargetHere && !isRobotHere && (
                                                    <Text style={styles.cellIcon}>{theme.targetIcon}</Text>
                                                )}
                                                {isRobotHere && (
                                                    <Text
                                                        style={[
                                                            styles.cellIcon,
                                                            styles.robotSprite,
                                                            {
                                                                transform: [{ rotate: `${robot.heading}deg` }],
                                                            },
                                                        ]}
                                                    >
                                                        {theme.sprite}
                                                    </Text>
                                                )}
                                            </View>
                                        );
                                    })}
                                </View>
                            ))}
                        </View>

                        {/* Status bar */}
                        <View style={styles.statusBar}>
                            <Text style={styles.statusText}>{statusMessage}</Text>
                        </View>

                        {/* Execution Controls */}
                        <View style={styles.simControls}>
                            <JuicyButton
                                label="RUN"
                                variant="primary"
                                size="medium"
                                onPress={runSimulation}
                                disabled={isRunning || hasCompleted || program.length === 0}
                                testID="run-algorithm-button"
                            />
                            <TouchableOpacity
                                style={styles.iconButton}
                                onPress={resetSimulation}
                                disabled={isRunning}
                                accessibilityLabel="Reset Simulation"
                                testID="reset-algorithm-button"
                            >
                                <RotateCcw size={22} color="#475569" />
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Block Script Stack & Block Palette */}
                    <View style={styles.scriptColumn}>
                        <Text style={styles.sectionHeader}>Program Algorithm ({program.length} blocks)</Text>
                        <ScrollView style={styles.programList} contentContainerStyle={styles.programListContent}>
                            {program.length === 0 ? (
                                <View style={styles.emptyScript}>
                                    <Text style={styles.emptyScriptText}>Tap blocks below to build your code sequence.</Text>
                                </View>
                            ) : (
                                program.map((block, idx) => {
                                    const isActive = activeBlockIndex === idx;
                                    return (
                                        <View
                                            key={block.id}
                                            style={[
                                                styles.codeBlockRow,
                                                { borderLeftColor: block.color || '#3B82F6' },
                                                isActive && styles.activeCodeBlockRow,
                                            ]}
                                        >
                                            <Text style={styles.codeBlockIndex}>{idx + 1}.</Text>
                                            <Text style={styles.codeBlockLabel}>{block.label}</Text>
                                            <TouchableOpacity
                                                style={styles.deleteBlockButton}
                                                onPress={() => removeBlock(idx)}
                                                disabled={isRunning}
                                                accessibilityLabel={`Remove block ${idx + 1}`}
                                            >
                                                <Trash2 size={16} color="#94A3B8" />
                                            </TouchableOpacity>
                                        </View>
                                    );
                                })
                            )}
                        </ScrollView>

                        {/* Block Toolbox Palette */}
                        <Text style={styles.sectionHeader}>Block Toolbox</Text>
                        <View style={styles.paletteContainer}>
                            {defaultBlocks.map((b) => (
                                <TouchableOpacity
                                    key={b.id}
                                    style={[styles.paletteBlock, { backgroundColor: b.color || '#3B82F6' }]}
                                    onPress={() => addBlock(b)}
                                    disabled={isRunning || hasCompleted}
                                    accessibilityLabel={`Add ${b.label}`}
                                    testID={`add-block-${b.type}`}
                                >
                                    <Plus size={16} color="#FFFFFF" />
                                    <Text style={styles.paletteBlockText}>{b.label}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                </View>
            </View>
        </StageCard>
    </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollWrapper: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContent: {
        padding: 12,
        alignItems: 'center',
    },
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 12,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badgeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
    },
    badgeDebug: {
        backgroundColor: '#FEE2E2',
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    badgeTextDebug: {
        color: '#DC2626',
    },
    themeTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: '#334155',
    },
    goalText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
        lineHeight: 22,
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    workspace: {
        flexDirection: 'row',
        gap: 16,
    },
    gridColumn: {
        flex: 1,
        alignItems: 'center',
        gap: 10,
    },
    gridContainer: {
        borderWidth: 3,
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
    },
    gridRow: {
        flexDirection: 'row',
    },
    gridCell: {
        width: 60,
        height: 60,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        justifyContent: 'center',
        alignItems: 'center',
    },
    targetCell: {
        backgroundColor: '#FEF08A',
    },
    cellIcon: {
        fontSize: 28,
    },
    robotSprite: {
        transform: [{ scale: 1.1 }],
    },
    statusBar: {
        width: '100%',
        backgroundColor: '#F8FAFC',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    statusText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
    simControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconButton: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#F1F5F9',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#CBD5E1',
    },
    scriptColumn: {
        flex: 1.2,
        gap: 8,
    },
    sectionHeader: {
        fontSize: 13,
        fontWeight: '800',
        color: '#64748B',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    programList: {
        height: 190,
        backgroundColor: '#F8FAFC',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    programListContent: {
        padding: 8,
        gap: 6,
    },
    emptyScript: {
        height: 160,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 20,
    },
    emptyScriptText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#94A3B8',
        textAlign: 'center',
    },
    codeBlockRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 8,
        borderLeftWidth: 5,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 8,
    },
    activeCodeBlockRow: {
        borderColor: '#F59E0B',
        backgroundColor: '#FEF3C7',
    },
    codeBlockIndex: {
        fontSize: 13,
        fontWeight: '800',
        color: '#94A3B8',
        width: 20,
    },
    codeBlockLabel: {
        flex: 1,
        fontSize: 14,
        fontWeight: '700',
        color: '#1E293B',
    },
    deleteBlockButton: {
        padding: 4,
    },
    paletteContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    paletteBlock: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        minHeight: 40,
    },
    paletteBlockText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
});
